/**
 * Suppresses iOS Safari's native pull-to-refresh.
 *
 * On iOS 26, swiping down while the document is already scrolled to the top
 * triggers a full-document reload. Because the app keeps its store/menu in
 * redux-persist, that reload throws the user into a multi-second skeleton
 * refetch — it reads as the page "freezing while scrolling".
 *
 * `html { overscroll-behavior-y: none }` (index.css) is the WebKit-documented
 * place to disable this, but it is unreliable on iOS Safari in practice, so we
 * back it up with a surgical touch guard rather than refactoring the whole
 * page into a fixed-height inner scroller (which would break the sticky header,
 * its window.scrollY shadow, the safe-area covers and the condensed header).
 *
 * The guard ONLY cancels a gesture that is unambiguously the pull-to-refresh
 * overscroll: a one-finger, vertically-dominant DOWNWARD drag that begins with
 * the document pinned at the very top and not inside an inner vertical
 * scroller (cart sidebar, product modal, bottom sheets). Normal reading
 * (finger moves up, dy < 0), pinch-zoom (2+ fingers) and the horizontal menu
 * tabs (dx-dominant) are never affected.
 */
export function installPullToRefreshGuard() {
  let startX = 0;
  let startY = 0;
  // Decided at touchstart and enforced from the FIRST touchmove — iOS only
  // suppresses a gesture if preventDefault() runs on its first move.
  let armed = false;

  const scroller = document.scrollingElement || document.documentElement;

  // True if the gesture started inside an element that can itself scroll
  // vertically — let the browser handle those natively.
  function hasScrollableYAncestor(node) {
    while (node && node !== document.body && node !== document.documentElement) {
      if (node.nodeType === 1) {
        const oy = getComputedStyle(node).overflowY;
        if (
          (oy === 'auto' || oy === 'scroll') &&
          node.scrollHeight > node.clientHeight
        ) {
          return true;
        }
      }
      node = node.parentNode;
    }
    return false;
  }

  document.addEventListener(
    'touchstart',
    (e) => {
      if (e.touches.length !== 1) {
        armed = false;
        return;
      }
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      armed = scroller.scrollTop <= 0 && !hasScrollableYAncestor(e.target);
    },
    { passive: true },
  );

  document.addEventListener(
    'touchmove',
    (e) => {
      if (!armed || e.touches.length !== 1) return;
      const dy = e.touches[0].clientY - startY;
      const dx = e.touches[0].clientX - startX;
      // Downward AND vertically dominant => the pull-to-refresh overscroll.
      // The dy > |dx| check keeps horizontal menu-tab swipes working.
      if (dy > 0 && dy > Math.abs(dx) && e.cancelable) {
        e.preventDefault();
      }
    },
    { passive: false },
  );
}
